import type { CustomerOrganizationType } from '@siliconx/shared';
import type { useAuth } from './authContext';

export type OnboardingPhase = 'tos' | 'add-asset' | 'done';

/**
 * SIL-1305 — single source of truth for "where is this individual-org user
 * in the first-run wizard": `TosGate`/`AddAssetGate`/`IndividualProfileGate`
 * (`routes/__root.tsx`) derive their own redirect predicates from this
 * instead of re-deriving the same condition three times (the same
 * two-independent-conditions hazard PR #1691 fixed for TosGate/
 * IndividualProfileGate — see `individualProfileGateWouldRedirect`'s
 * docstring there). `AppShell`'s setup rail, `NavCommandPalette`,
 * `AddAssetSuccessPanel`'s `firstRun` mode, and `/individual-profile`'s
 * profile banner all read this same phase rather than re-checking
 * canSell/canBuy/hasAssets independently.
 *
 * Rules, in order — ToS first for every org type; then an exhaustive switch
 * over the org type (SIL-2533: adding a CustomerOrganizationType is a compile
 * error here until it states its first-run phase). business, broker and
 * reseller are `'done'` after ToS - the business-org variant of this wizard is
 * explicitly out of scope (deferred to a follow-up issue). An unresolved
 * (null) org type is also `'done'`: this is presentation only, every server
 * gate already denies a null org type, and a Setup rail an unprovisioned
 * account cannot complete would be worse than none.
 */
export function onboardingPhase(
  auth: Pick<
    ReturnType<typeof useAuth>,
    'tosAccepted' | 'organizationType' | 'canSell' | 'canBuy' | 'hasAssets'
  >,
): OnboardingPhase {
  if (!auth.tosAccepted) return 'tos';
  if (phaseForOrgType(auth.organizationType) === 'done') return 'done';
  if (auth.canSell && auth.canBuy) return 'done';
  if (!auth.hasAssets) return 'add-asset';
  // SIL-1812: profile completion is deferred to the Exchange pages
  // (`individualProfileGateWouldRedirect`), so an individual with assets is done.
  return 'done';
}

/** Exhaustive over CustomerOrganizationType (+ null). 'individual' continues into the wizard. */
function phaseForOrgType(orgType: CustomerOrganizationType | null): 'done' | 'wizard' {
  switch (orgType) {
    case 'individual':
      return 'wizard';
    case 'business':
    case 'broker':
    case 'reseller':
    case null:
      return 'done';
    default: {
      const _exhaustive: never = orgType;
      void _exhaustive;
      return 'done';
    }
  }
}
